/**
 * Clock In Activity Page - Microsoft 365 Style
 * Track employee clock-in/clock-out activities
 */

import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  Input,
  Text,
  Badge,
  Avatar,
  Field,
  Button,
  Spinner,
} from '@fluentui/react-components';
import { SearchRegular, PersonRegular, CalendarRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../components/layout';
import { TablePagination } from '../../components/common';
import {
  ApiClient,
  newToast,
  fullDate,
  getErrorMessage,
  getEmployeeImageUrl,
} from '../../configurations/index.config';
import type { PaginationMeta } from '../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  filterBar: {
    marginBottom: '16px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
});

interface ClockRecord {
  id: string;
  first_name: string;
  last_name: string;
  unique_emp_id: string;
  clock_in_date: string;
  clock_in_time: string;
  clock_out_date?: string;
  clock_out_time?: string;
  clocking_type: string;
  status: string;
  face_url?: string;
}

export const ClockInFluentUI: React.FC = () => {
  const styles = useStyles();
  const [clockRecords, setClockRecords] = useState<ClockRecord[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchClockRecords();
  }, [currentPage, itemsPerPage, startDate, endDate]);

  const fetchClockRecords = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }

      const response = await ApiClient.get('/clocking-activity', { params });
      if (response.data.status === 'success') {
        setClockRecords(response.data.data || []);
        setPagination(response.data.pagination);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Clocking Activity</Text>
        <Text className={styles.pageSubtitle}>
          {loading ? 'Loading...' : `${pagination?.totalItems || 0} clock records`}
        </Text>
      </div>

      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <Field label="Start Date">
          <Input
            type="date"
            contentBefore={<CalendarRegular />}
            value={startDate}
            onChange={e => {
              setStartDate(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Field>
        <Field label="End Date">
          <Input
            type="date"
            contentBefore={<CalendarRegular />}
            value={endDate}
            onChange={e => {
              setEndDate(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Field>
      </div>

      {/* Clock Records Table */}
      <ContentCard noPadding>
        {loading ? (
          <div className={styles.emptyState}>
            <Spinner label="Loading clock records..." />
          </div>
        ) : clockRecords.length === 0 ? (
          <div className={styles.emptyState}>
            <PersonRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No clock records found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              Try adjusting your date range filters
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>Employee</th>
                <th className={styles.th}>ID</th>
                <th className={styles.th}>Clock In</th>
                <th className={styles.th}>Clock Out</th>
                <th className={styles.th}>Type</th>
                <th className={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {clockRecords.map(record => (
                <tr key={record.id} className={styles.tr}>
                  <td className={styles.td}>
                    <div className={styles.userCell}>
                      <Avatar
                        name={`${record.first_name} ${record.last_name}`}
                        image={
                          record.face_url
                            ? { src: getEmployeeImageUrl(record.face_url) }
                            : undefined
                        }
                        color="brand"
                        size={32}
                      />
                      <Text weight="semibold">
                        {record.first_name} {record.last_name}
                      </Text>
                    </div>
                  </td>
                  <td className={styles.td}>
                    <Badge appearance="outline">{record.unique_emp_id}</Badge>
                  </td>
                  <td className={styles.td}>
                    {record.clock_in_date && (
                      <div>
                        <div>{fullDate(record.clock_in_date)}</div>
                        <div style={{ fontSize: '12px', color: '#605e5c' }}>
                          {record.clock_in_time || ''}
                        </div>
                      </div>
                    )}
                  </td>
                  <td className={styles.td}>
                    {record.clock_out_date ? (
                      <div>
                        <div>{fullDate(record.clock_out_date)}</div>
                        <div style={{ fontSize: '12px', color: '#605e5c' }}>
                          {record.clock_out_time || ''}
                        </div>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={styles.td}>
                    <Badge appearance="tint" color="informative">
                      {record.clocking_type || 'Standard'}
                    </Badge>
                  </td>
                  <td className={styles.td}>
                    {record.clock_out_date ? (
                      <Badge appearance="tint" color="success">
                        Completed
                      </Badge>
                    ) : (
                      <Badge appearance="tint" color="warning">
                        Clocked In
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && pagination && clockRecords.length > 0 && (
          <TablePagination
            totalItems={pagination.totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </ContentCard>
    </div>
  );
};

export default ClockInFluentUI;
